/**
 * "orboto AI" usage page (OCP-496, epic OCP-395; allowance + overage OCP-572).
 *
 * The customer-facing counterpart to the operator-side metering (OCP-400): it
 * answers "what am I being charged for?" so an "orboto AI usage" wallet debit
 * is never a mystery. Per OCP-258, every paying tier carries an INCLUDED
 * per-seat monthly AI budget consumed first; beyond it, wallet-funded overage
 * runs only after the customer activates it here (the same opt-in every AI
 * provider uses).
 *
 * Shows: the included-allowance state (used / total, next reset), the overage
 * switch + monthly cap, tokens consumed + spend over a selectable period, the
 * current price per 1M embedding tokens (so the math is transparent), a
 * per-workspace breakdown, and - plainly - the paused state when a workspace's
 * key was blocked at a spend boundary, with the exact ways it resumes. Before
 * any usage, the empty state explains where usage will appear.
 *
 * The data comes ONLY from OCP's own persistence via /v1/portal/ai/usage +
 * /v1/portal/me/ai-overage-settings - the portal never talks to the AI
 * gateway. The wallet line item and this page use the SAME label
 * ("orboto AI usage") so they tell one story.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import i18n, { intlTag } from '../i18n';
import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';
import { fmtNumber, fmtOrbotoTokens } from '../lib/format-money';
import { AccountAiKeysSection } from '../components/AccountAiKeysSection';
import { usePortalAccess } from '../lib/use-portal-access';

type Period = '7d' | '30d' | '90d' | 'all';

/** Period ids; the label is `ai-usage:period.<id>` (OCP-656). */
const PERIODS: Period[] = ['7d', '30d', '90d', 'all'];

/** Deep-link straight to the wallet top-up view in Billing. */
const WALLET_LINK = '/billing?tab=wallet';

interface OverageSettingsWire {
  overage_mode: 'hard_gate' | 'use_credits';
  monthly_cap_eur_cents: number;
  allowance: {
    month: string;
    grant_microcents: number;
    consumed_microcents: number;
    remaining_microcents: number;
    next_reset_date: string;
    entitlements: Array<{ tier: string; seats: number; source: 'subscription' | 'code' }>;
  };
  overage_this_month_microcents: number;
  wallet_balance_microcents: number;
  would_stop: boolean;
}

interface UsageWire {
  period: Period;
  /** OCP-660 - the published orboto Token rate; the AI amounts render in tokens once the AGB V4 package is released. */
  orboto_token?: { microcents_per_token: number } | null;
  price_microcents_per_mtok: number | null;
  /** OCP-584 - per-chat-model price table (empty = chat not enabled). */
  model_prices: Record<string, { in_microcents_per_mtok: number; out_microcents_per_mtok: number }>;
  /** OCP-667 - every reachable model with its customer price per 1M tokens (microcents + orboto Token). */
  price_list?: Array<{ model: string; display_name: string; capabilities: string[]; in_microcents_per_mtok: number; out_microcents_per_mtok: number; in_orboto_token_per_1m: number | null; out_orboto_token_per_1m: number | null }>;
  /** OCP-584 - lifetime per-model usage across the workspaces. */
  models: Array<{ model: string; total_tokens: number; amount_microcents: number }>;
  /** OCP-778 - lifetime per-model usage of the account's own API keys. */
  api_key_models?: Array<{ model: string; call_class: 'embedding' | 'chat' | 'unpriced'; total_tokens: number; amount_microcents: number }>;
  usage_note: string;
  summary: {
    period_tokens: number;
    period_microcents: number;
    period_cents: number;
    total_tokens: number;
    total_microcents_debited: number;
    owed_microcents: number;
    gated: boolean;
  };
  workspaces: Array<{
    tenant_id: string;
    subdomain: string;
    custom_domain: string | null;
    tenant_url: string;
    ai_enabled: boolean;
    period_tokens: number;
    period_microcents: number;
    total_tokens: number;
    total_microcents_debited: number;
    owed_microcents: number;
    gated: boolean;
    blocked_at: string | null;
    /** OCP-627 - why the gate closed + when the account was mailed. */
    blocked_reason: 'allowance_exhausted' | 'overage_cap_reached' | 'wallet_empty' | null;
    gate_notice_sent_at: string | null;
    last_sweep_at: string | null;
  }>;
}

/**
 * OCP-627 - the durable notice text per gate reason (the mail says the same).
 * Reads the active language through i18next directly so callers outside a
 * component (OverviewPage banner) keep the plain signature (OCP-656).
 */
export function gateReasonCopy(reason: UsageWire['workspaces'][number]['blocked_reason']): { why: string; remedy: string; cta: string; to: string } {
  const key = reason === 'allowance_exhausted' || reason === 'overage_cap_reached' || reason === 'wallet_empty' ? reason : 'default';
  const to = key === 'allowance_exhausted' || key === 'overage_cap_reached' ? '/ai' : '/billing?tab=wallet';
  return {
    why: i18n.t(`ai-usage:gate.${key}.why`),
    remedy: i18n.t(`ai-usage:gate.${key}.remedy`),
    cta: i18n.t(`ai-usage:gate.${key}.cta`),
    to,
  };
}

export function AiUsagePage() {
  const { t } = useTranslation('ai-usage');
  const [period, setPeriod] = useState<Period>('30d');
  const [data, setData] = useState<UsageWire | null>(null);
  // OCP-668 (launch): orboto Token is the unit whenever a rate is published - no dark-launch switch.
  const tokenRate = data?.orboto_token?.microcents_per_token ? data.orboto_token.microcents_per_token : null;
  const [settings, setSettings] = useState<OverageSettingsWire | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (p: Period, signal?: AbortSignal) => {
    try {
      const r = await api<UsageWire>(`/v1/portal/ai/usage?period=${p}`, { signal });
      setData(r);
      setError(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof ApiError ? t('loadFailedHttp', { status: err.status }) : t('loadFailed'));
    }
  }, [t]);

  // OCP-572 - the allowance + overage-switch state (separate endpoint, its
  // own failure never blanks the usage view).
  const loadSettings = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await api<OverageSettingsWire>('/v1/portal/me/ai-overage-settings', { signal });
      setSettings(r);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setSettings(null);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(period, ac.signal);
    return () => ac.abort();
  }, [load, period]);

  useEffect(() => {
    const ac = new AbortController();
    void loadSettings(ac.signal);
    return () => ac.abort();
  }, [loadSettings]);

  if (error) {
    return (
      <div className="mx-auto max-w-4xl">
        <Header />
        <div
          role="alert"
          className="mt-6 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-4xl">
        <Header />
        <p className="mt-6 text-sm text-gray-500">{t('loading')}</p>
      </div>
    );
  }

  const { summary } = data;
  const hasUsage = summary.total_tokens > 0 || summary.owed_microcents > 0 || summary.gated;
  const priceLabel =
    data.price_microcents_per_mtok == null
      ? t('price.notSet')
      : t('price.perMillion', { price: formatMicrocents(data.price_microcents_per_mtok, 'EUR') });

  return (
    <div className="mx-auto max-w-4xl">
      <Header />

      {summary.gated && (
        <div
          role="alert"
          data-testid="ai-gated-banner"
          className="mt-6 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-800 dark:text-red-300"
        >
          <p className="font-semibold">{t('gated.title')}</p>
          {/* OCP-627 - one line per paused workspace with the reason and the remedy; the same text went out by mail. */}
          {data.workspaces
            .filter((w) => w.gated)
            .map((w) => {
              const copy = gateReasonCopy(w.blocked_reason);
              return (
                <p key={w.tenant_id} className="mt-1" data-testid={`ai-gate-notice-${w.tenant_id}`}>
                  <span className="font-medium">{w.custom_domain ?? `${w.subdomain}.orbo.to`}</span>: {copy.why} {copy.remedy}
                  {w.gate_notice_sent_at ? t('gated.mailed', { date: fmtDate(w.gate_notice_sent_at) }) : ''}
                </p>
              );
            })}
          <p className="mt-1">
            {t('gated.openAmountBefore')}{' '}
            <span className="font-medium"><AiAmount microcents={summary.owed_microcents} rate={tokenRate} /></span>
            {t('gated.openAmountAfter')}
          </p>
          <Link
            to={WALLET_LINK}
            data-testid="ai-topup-link"
            className="mt-2 inline-block rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
          >
            {t('gated.topUp')}
          </Link>
        </div>
      )}

      {/* OCP-659 - AGB V4 § 3.3 / 3.4 / 3.5: the AI-output notice + the training consent state. */}
      <AiNoticeSection />

      {/* OCP-572 - included allowance + the overage switch. */}
      {settings && (
        <AllowanceSection settings={settings} tokenRate={tokenRate} onChanged={() => void loadSettings()} />
      )}

      {hasUsage ? (
        <>
          {/* Period selector */}
          <nav
            className="mt-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800"
            aria-label={t('period.aria')}
          >
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                aria-pressed={period === p}
                data-testid={`ai-period-${p}`}
                className={`-mb-px border-b-2 px-3 py-2 text-sm transition-colors ${
                  period === p
                    ? 'border-brand-500 text-brand-700'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                {t(`period.${p}`)}
              </button>
            ))}
          </nav>

          {/* Summary tiles */}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatTile
              testid="ai-summary-tokens"
              label={t('summary.tokens')}
              value={formatTokens(summary.period_tokens)}
            />
            <StatTile
              testid="ai-summary-spend"
              label={t('summary.spend')}
              value={<AiAmount microcents={summary.period_microcents} rate={tokenRate} />}
            />
            <StatTile testid="ai-price" label={t('summary.price')} value={priceLabel} />
          </div>

          {/* Per-workspace breakdown */}
          <section className="mt-6">
            <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('workspaces.title')}
            </h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('workspaces.colWorkspace')}</th>
                    <th className="px-3 py-2 text-right">{t('workspaces.colTokens')}</th>
                    <th className="px-3 py-2 text-right">{t('workspaces.colSpend')}</th>
                    <th className="px-3 py-2 text-left">{t('workspaces.colStatus')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {data.workspaces.map((w) => (
                    <tr key={w.tenant_id} data-testid={`ai-workspace-${w.subdomain}`}>
                      <td className="px-3 py-2">
                        <a
                          href={w.tenant_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-700 hover:underline"
                        >
                          {w.custom_domain ?? w.subdomain}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                        {formatTokens(w.period_tokens)}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                        <AiAmount microcents={w.period_microcents} rate={tokenRate} />
                      </td>
                      <td className="px-3 py-2">
                        {w.gated ? (
                          <span className="inline-flex items-center gap-1 rounded bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                            {w.blocked_reason === 'allowance_exhausted'
                              ? t('workspaces.pausedBudget')
                              : w.blocked_reason === 'overage_cap_reached'
                                ? t('workspaces.pausedCap')
                                : t('workspaces.pausedTopUp')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                            {t('workspaces.active')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.models.length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t('models.title')}
                </h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">{t('models.colModel')}</th>
                        <th className="px-3 py-2 text-right">{t('models.colTokens')}</th>
                        <th className="px-3 py-2 text-right">{t('models.colSpend')}</th>
                        <th className="px-3 py-2 text-right">{t('models.colPrice')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {data.models.map((m) => {
                        const p = data.model_prices[m.model];
                        return (
                          <tr key={m.model} data-testid={`ai-model-${m.model}`}>
                            <td className="px-3 py-2 font-mono text-xs">{m.model}</td>
                            <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                              {formatTokens(m.total_tokens)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">
                              {formatMicrocents(m.amount_microcents, 'EUR')}
                            </td>
                            <td className="px-3 py-2 text-right text-xs text-gray-500">
                              {p
                                ? `${fmtPrice(p.in_microcents_per_mtok, tokenRate)} / ${fmtPrice(p.out_microcents_per_mtok, tokenRate)}`
                                : data.price_microcents_per_mtok != null
                                  ? fmtPrice(data.price_microcents_per_mtok, tokenRate)
                                  : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <p className="mt-3 text-xs text-gray-500" data-testid="ai-wallet-note">
              {t('walletNote.before')}{' '}
              <Link to={WALLET_LINK} className="text-brand-700 hover:underline">
                {t('walletNote.link')}
              </Link>{' '}
              {t('walletNote.after', { note: data.usage_note })}
            </p>
          </section>
        </>
      ) : (
        <section className="mt-6 space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('empty.priceLabel')}</p>
            <p
              data-testid="ai-price"
              className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100"
            >
              {priceLabel}
            </p>
          </div>
          <div
            data-testid="ai-empty"
            className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500"
          >
            {t('empty.body')}
          </div>
        </section>
      )}

      {data.price_list && data.price_list.length > 0 && (
        <div className="mt-4" data-testid="ai-price-list">
          <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{t('priceList.title')}</h3>
          <p className="mb-2 text-xs text-gray-500">
            {tokenRate ? t('priceList.hintToken', { eur: formatMicrocents(tokenRate, 'EUR') }) : t('priceList.hintEur')}
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t('priceList.colModel')}</th>
                  <th className="px-3 py-2 text-left">{t('priceList.colKind')}</th>
                  <th className="px-3 py-2 text-right">{t('priceList.colIn')}</th>
                  <th className="px-3 py-2 text-right">{t('priceList.colOut')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {data.price_list.map((m) => (
                  <tr key={m.model} data-testid={`ai-price-${m.model}`}>
                    <td className="px-3 py-2">
                      <span className="text-gray-900 dark:text-gray-100">{m.display_name}</span>
                      {m.display_name !== m.model && <span className="ml-1 font-mono text-xs text-gray-500">{m.model}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{m.capabilities.map((c) => t(`priceList.kind.${c}`, { defaultValue: c })).join(', ')}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{fmtPrice(m.in_microcents_per_mtok, tokenRate)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">{m.capabilities.includes('embedding') && m.out_microcents_per_mtok === 0 ? '-' : fmtPrice(m.out_microcents_per_mtok, tokenRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.api_key_models && data.api_key_models.length > 0 && (
        <div className="mt-4" data-testid="ai-api-key-models">
          <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{t('apiKeyModels.title')}</h3>
          <p className="mb-2 text-xs text-gray-500">{t('apiKeyModels.hint')}</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">{t('models.colModel')}</th>
                  <th className="px-3 py-2 text-left">{t('apiKeyModels.colKind')}</th>
                  <th className="px-3 py-2 text-right">{t('models.colTokens')}</th>
                  <th className="px-3 py-2 text-right">{t('models.colSpend')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {data.api_key_models.map((m) => (
                  <tr key={`${m.call_class}:${m.model}`} data-testid={`ai-api-key-model-${m.model}`}>
                    <td className="px-3 py-2 font-mono text-xs">{m.model}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{t(`apiKeyModels.kind.${m.call_class}`)}</td>
                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatTokens(m.total_tokens)}</td>
                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatMicrocents(m.amount_microcents, 'EUR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* OCP-497 - self-service account AI keys (create/list/revoke + snippet). */}
      <AccountAiKeysSection />
    </div>
  );
}

function Header() {
  const { t } = useTranslation('ai-usage');
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
        {t('title')}
      </h1>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('intro')}</p>
    </div>
  );
}

/**
 * OCP-572 - the included-allowance card + overage switch. The switch is the
 * customer-facing spend decision (billing-only role may flip it); the cap is
 * the anti-runaway bound in EUR/month.
 */
function AllowanceSection({
  tokenRate,
  settings,
  onChanged,
}: {
  tokenRate: number | null;
  settings: OverageSettingsWire;
  onChanged: () => void;
}) {
  const { t } = useTranslation('ai-usage');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [capInput, setCapInput] = useState(String(settings.monthly_cap_eur_cents / 100));

  const a = settings.allowance;
  const pct =
    a.grant_microcents > 0
      ? Math.min(100, Math.round((a.consumed_microcents / a.grant_microcents) * 100))
      : 0;
  const overageOn = settings.overage_mode === 'use_credits';

  async function patch(body: { overage_mode?: 'hard_gate' | 'use_credits'; monthly_cap_eur_cents?: number }) {
    setSaving(true);
    setSaveError(null);
    try {
      await api('/v1/portal/me/ai-overage-settings', { method: 'PATCH', body });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setSaveError(t('allowance.forbidden'));
      } else {
        setSaveError(t('allowance.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      data-testid="ai-allowance-section"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('allowance.title')}
        </h2>
        <span className="text-xs text-gray-500">
          {t('allowance.resets', { date: a.next_reset_date })}
          {a.entitlements.length > 0 &&
            ` · ${a.entitlements
              .map((e) => t('allowance.seats', { tier: e.tier, count: e.seats }))
              .join(' + ')}`}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500" data-testid="ai-allowance-expiry">
        {t('allowance.expiry')}
      </p>

      {a.grant_microcents > 0 ? (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <span data-testid="ai-allowance-used" className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {tokenRate ? fmtOrbotoTokens(a.consumed_microcents, tokenRate) : formatMicrocents(a.consumed_microcents, 'EUR')}
            </span>
            <span className="text-sm text-gray-500">
              {t('allowance.ofThisMonth', { total: tokenRate ? fmtOrbotoTokens(a.grant_microcents, tokenRate) : formatMicrocents(a.grant_microcents, 'EUR') })}
              {tokenRate ? ` · ${t('token.rate', { eur: formatMicrocents(tokenRate, 'EUR') })}` : ''}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
            <div
              className={`h-full rounded ${pct >= 80 ? 'bg-amber-500' : 'bg-brand-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-gray-500" data-testid="ai-allowance-none">
          {t('allowance.none')}
        </p>
      )}

      <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('allowance.overageTitle')}
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              {overageOn ? t('allowance.overageOn') : t('allowance.overageOff')}
            </p>
          </div>
          <button
            type="button"
            disabled={saving}
            data-testid="ai-overage-toggle"
            onClick={() =>
              void patch({ overage_mode: overageOn ? 'hard_gate' : 'use_credits' })
            }
            className={`rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
              overageOn
                ? 'border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                : 'bg-brand-500 text-white hover:bg-brand-600'
            }`}
          >
            {overageOn ? t('allowance.deactivate') : t('allowance.activate')}
          </button>
        </div>

        {overageOn && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="ai-overage-cap" className="text-xs text-gray-600 dark:text-gray-400">
              {t('allowance.capLabel')}
            </label>
            <input
              id="ai-overage-cap"
              type="number"
              min={0}
              step="1"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              className="w-24 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
            />
            <button
              type="button"
              disabled={saving}
              data-testid="ai-overage-cap-save"
              onClick={() => {
                const eur = Number(capInput);
                if (!Number.isFinite(eur) || eur < 0) {
                  setSaveError(t('allowance.capInvalid'));
                  return;
                }
                void patch({ monthly_cap_eur_cents: Math.round(eur * 100) });
              }}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50"
            >
              {t('allowance.saveCap')}
            </button>
            <span className="text-xs text-gray-500">
              {t('allowance.usedThisMonth', { amount: formatMicrocents(settings.overage_this_month_microcents, 'EUR') })}
              {settings.monthly_cap_eur_cents === 0 && t('allowance.capZero')}
            </span>
          </div>
        )}

        {saveError && (
          <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
            {saveError}
          </p>
        )}
      </div>
    </section>
  );
}

function StatTile({ testid, label, value }: { testid: string; label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div
        data-testid={testid}
        className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100"
      >
        {value}
      </div>
    </div>
  );
}

function formatTokens(tokens: number): string {
  return fmtNumber(tokens, {}, String(tokens));
}

// Wallet amounts are microcents (1 cent = 10,000). Show sub-cent precision (up
// to 4 decimals) when an amount is not a whole cent so a small AI debit renders
// as e.g. EUR 0.0300, not EUR 0.00. Mirrors BillingPage's wallet formatter.
/** OCP-667 - a price per 1M tokens: orboto Token once a rate is published (AGB V4), else EUR. */
function fmtPrice(microcentsPerMtok: number, tokenRate: number | null): string {
  if (tokenRate) {
    const tokens = Math.round((microcentsPerMtok / tokenRate) * 100) / 100;
    return `${tokens.toLocaleString(undefined, { maximumFractionDigits: 2 })} Token`;
  }
  return formatMicrocents(microcentsPerMtok, 'EUR');
}

function formatMicrocents(microcents: number, currency: string): string {
  const euros = microcents / 1_000_000;
  const wholeEuro = microcents % 1_000_000 === 0;
  const subCent = microcents % 10_000 !== 0;
  try {
    return new Intl.NumberFormat(intlTag(), {
      style: 'currency',
      currency,
      minimumFractionDigits: wholeEuro ? 0 : 2,
      maximumFractionDigits: subCent ? 4 : 2,
    }).format(euros);
  } catch {
    return `${euros.toFixed(subCent ? 4 : 2)} ${currency}`;
  }
}

// ---- OCP-659 - AGB V4 § 3 notices ---------------------------------------

interface AiNoticeWire {
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  training: Array<{ tenant_id: string; subdomain: string; state: 'opted_in' | 'opted_out' | 'none'; at: string | null }>;
}

function isAiNoticeWire(v: unknown): v is AiNoticeWire {
  return !!v && typeof v === 'object' && 'acknowledged_at' in v && Array.isArray((v as { training?: unknown }).training);
}

/**
 * The AI-output notice the AGB require the customer to have read (3.3 / 3.4)
 * - shown permanently, acknowledged once per account by an administrator -
 * plus the training-consent state per workspace with the 3.5 consequence
 * (revocation only for the future).
 */
function AiNoticeSection() {
  const { t } = useTranslation('ai-usage');
  const { canConcludeContract } = usePortalAccess();
  const [notice, setNotice] = useState<AiNoticeWire | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const r = await api<unknown>('/v1/portal/me/ai-notice', { signal: ac.signal });
        if (isAiNoticeWire(r)) setNotice(r);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // the notice text still renders; only the acknowledgement state is unknown
      }
    })();
    return () => ac.abort();
  }, []);

  async function acknowledge(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const r = await api<unknown>('/v1/portal/me/ai-notice/ack', { method: 'POST' });
      if (isAiNoticeWire(r)) setNotice(r);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
      data-testid="ai-notice"
    >
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('notice.title')}</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('notice.body')}</p>
      {notice && !notice.acknowledged_at && canConcludeContract && (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void acknowledge()}
            disabled={busy}
            data-testid="ai-notice-ack"
            className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {busy ? t('notice.ackPending') : t('notice.ack')}
          </button>
          {failed && <span className="text-xs text-red-600">{t('notice.ackFailed')}</span>}
        </div>
      )}
      {notice?.acknowledged_at && (
        <p className="mt-2 text-xs text-gray-500" data-testid="ai-notice-acked">
          {t('notice.acked', { date: fmtDate(notice.acknowledged_at), by: notice.acknowledged_by ?? '-' })}
        </p>
      )}
      {notice && notice.training.length > 0 && (
        <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-3" data-testid="ai-training-consent">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('notice.trainingTitle')}</h3>
          <ul className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-gray-300">
            {notice.training.map((w) => (
              <li key={w.tenant_id}>
                {t(`notice.training_${w.state}`, { tenant: w.subdomain, date: w.at ? fmtDate(w.at) : '-' })}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-gray-500">{t('notice.trainingFuture')}</p>
        </div>
      )}
    </section>
  );
}

/**
 * OCP-660 - an AI amount: orboto Token first (AGB V4 § 3.1), the EUR value in
 * small print, once a rate is published (always on since OCP-668);
 * plain EUR before that.
 */
function AiAmount({ microcents, rate, testid }: { microcents: number; rate: number | null; testid?: string }) {
  const { t } = useTranslation('ai-usage');
  if (!rate) return <span data-testid={testid}>{formatMicrocents(microcents, 'EUR')}</span>;
  return (
    <span data-testid={testid}>
      {fmtOrbotoTokens(microcents, rate)} <span className="text-xs font-normal text-gray-500">{t('token.eur', { eur: formatMicrocents(microcents, 'EUR') })}</span>
    </span>
  );
}
